import React from 'react';
import { Box, Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';

import { FormHelperText, FormInput, FormLabel } from '../../_core/_ui/forms.component';
import { IMilestoneEvaluation, IMilestoneInput } from '../../../lib/helpers/player-milestone.helpers';
import {
  MeasurementSystem,
  PLAYER_MILESTONE_TYPE_IMPERIAL_UNITS,
  PLAYER_MILESTONE_TYPE_LABELS,
  PLAYER_MILESTONE_TYPE_UNITS,
} from '../../../lib/types';
import { MilestoneChartButton } from './milestone-chart-button.component';

interface IMilestoneFieldProps {
  type: PlayerMilestoneType;
  system: MeasurementSystem;
  input: IMilestoneInput;
  error?: string | null;
  evaluation?: IMilestoneEvaluation;
  onChange: (input: IMilestoneInput) => void;
}

// One editable measurement. Always shows a single unit (metric or imperial per the
// section toggle); imperial height is the only case that needs two inputs (ft +
// in). Values are held by the parent section and committed only on Save.
export const MilestoneField: React.FC<IMilestoneFieldProps> = ({ type, system, input, error, evaluation, onChange }) => {
  const id = `milestone-${type}`;
  const label = PLAYER_MILESTONE_TYPE_LABELS[type];
  const isImperialHeight = type === 'height' && system === 'imperial';
  const unit = system === 'metric' ? PLAYER_MILESTONE_TYPE_UNITS[type] : PLAYER_MILESTONE_TYPE_IMPERIAL_UNITS[type];

  return (
    <FormControl margin="normal" fullWidth component="div" error={!!error}>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {isImperialHeight ? (
          <Box flexGrow={1} display="flex" alignItems="center" style={{ gap: 8 }}>
            <FormInput
              fullWidth
              id={id}
              type="number"
              placeholder="ft"
              value={input.value}
              onChange={(event) => onChange({ ...input, value: event.target.value })}
            />
            <Typography variant="body2" color="textSecondary">ft</Typography>
            <FormInput
              fullWidth
              id={`${id}-inches`}
              type="number"
              placeholder="in"
              value={input.inches}
              onChange={(event) => onChange({ ...input, inches: event.target.value })}
            />
            <Typography variant="body2" color="textSecondary">in</Typography>
          </Box>
        ) : (
          <>
            <Box flexGrow={1}>
              <FormInput
                fullWidth
                id={id}
                type="number"
                value={input.value}
                onChange={(event) => onChange({ ...input, value: event.target.value })}
              />
            </Box>
            <Typography variant="body2" color="textSecondary">{unit}</Typography>
          </>
        )}
        <MilestoneChartButton type={type} evaluation={evaluation} />
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
